import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { getCampus, ALL_CAMPUSES } from './campus';

/**
 * The campus this screen is working in.
 *
 * ═══ WHAT THIS REPLACES ═══
 *
 * ⚠ SIX SCREENS USED TO GUESS, BY FETCHING ONE STUDENT:
 *
 *     api.get('/api/students?take=1').then(r => setInstId(r[0]?.institutionId ?? null))
 *
 * "Which campus am I? — whichever one the first student row happens to belong to." On a
 * single-campus school that is correct by accident. On a trust running a school and a college it is
 * arbitrary: the answer depends on which row the database returns first, it can differ between page
 * loads, and a house created on the Houses page could land on the wrong campus entirely.
 *
 * It also cost a whole student query on every page load to read one field.
 *
 * ⚠ THE ORDER OF PREFERENCE IS DELIBERATE:
 *
 *   1. `institutionId` on the caller's own token — a campus-level role IS their campus, and a
 *      signed claim is a fact rather than a hint.
 *   2. What a TrustAdmin picked in the top bar, when it is a specific campus.
 *   3. Null — "every campus", or genuinely unknown. Callers that need one campus must say so.
 *
 * ⚠ NULL IS A REAL ANSWER AND EVERY CALLER MUST HANDLE IT. A trust admin looking across campuses,
 * a single-campus school that has never set the field, a token minted before this existed. A screen
 * that needs exactly one campus disables its action and says why — it does NOT pick one.
 */
export function useCampus(): { institutionId: string | null; loading: boolean } {
  const [id, setId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get<{ institutionId?: string | null }>('/api/auth/me')
      .then((me) => {
        if (!alive) return;
        const chosen = getCampus();
        setId(
          me?.institutionId                                        // 1. pinned by the token
          ?? (chosen && chosen !== ALL_CAMPUSES ? chosen : null),  // 2. picked in the top bar
        );
      })
      .catch(() => { if (alive) setId(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return { institutionId: id, loading };
}

/**
 * The campus to work in, WITH THE UNAMBIGUOUS CASE RESOLVED.
 *
 * ⚠ WHY THIS EXISTS ALONGSIDE useCampus. `useCampus` returns null for a single-campus school whose
 * token never carried the claim — which is most schools, and every token minted before the field
 * existed. A screen that stops there prints "no campus selected" to a principal who has exactly one
 * campus and no picker to choose it with. That is a dead screen, and it looks like a broken product.
 *
 * ⚠ IT IS NOT THE OLD GUESS COMING BACK. The screens this replaced read `/api/students?take=1` and
 * used whichever campus the first student row happened to belong to — arbitrary, and different
 * between page loads. This asks the institutions list and uses it ONLY when there is exactly one.
 * One institution has one answer; that is determinate, not a guess.
 *
 * ⚠ WITH TWO OR MORE IT STAYS NULL AND SAYS SO. `choices` is returned precisely so the caller can
 * render "which campus?" instead of silently operating on the wrong one. Picking the first of two
 * would be the original bug wearing a new name.
 */
export function useResolvedCampus(): {
  institutionId: string | null;
  choices: Array<{ id: string; name: string }>;
  loading: boolean;
} {
  const { institutionId, loading: campusLoading } = useCampus();
  const [choices, setChoices] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (campusLoading) return;
    if (institutionId) { setLoading(false); return; }
    let alive = true;
    api.get<Array<{ id: string; name: string; isActive?: boolean }>>('/api/institutions')
      .then((r) => {
        if (!alive) return;
        // ⚠ Array.isArray, not a null check: an unstubbed or erroring endpoint answers `{}`, which
        // is truthy, and `{}.filter` throws one line later. This exact shape has bitten six screens.
        const list = Array.isArray(r) ? r.filter((i) => i.isActive !== false) : [];
        setChoices(list.map((i) => ({ id: i.id, name: i.name })));
      })
      .catch(() => { if (alive) setChoices([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [institutionId, campusLoading]);

  return {
    institutionId: institutionId ?? (choices.length === 1 ? choices[0].id : null),
    choices,
    loading: campusLoading || loading,
  };
}
